/**
 * Profile — the user's OWN profile, read-only (PPL-395).
 * Route: /profile (ProtectedRoute). Edit is a button on the page, not the page.
 *
 * Mirrors the app's "My Profile" ListHeader section for section
 * (app/src/screens/Tabs/UserProfileScreen.js): hero card → intel rows →
 * badges → details grid → action buttons → underline tabs.
 *
 * IDENTITY REGISTER: this is the PERSONAL half, so firstName + lastName next
 * to the @handle is correct here. The messages tab inside it is the ANONYMOUS
 * half and stays handle-only, and the public profile it links to (/user/:uid)
 * is untouched by this page.
 *
 * Two app rows have no web surface and are deliberately absent: the "calendar
 * sources" intel row (web has no calendar route — calendar sync is device-side)
 * and the "viewed profile" button (web has no who-viewed-me page). The views
 * COUNT still shows, because getProfileViewsEurW's countOnly branch returns
 * before the premium gate.
 *
 * One deliberate DEVIATION from the app: "interested in" and "interests" are
 * shown whenever the field is set, without the app's lookingFor gates
 * (UserProfileScreen.js shows interestedIn only for 'romantic_connection' and
 * interests only for 'shared_interest_groups'). This page is visible ONLY to
 * its owner, web's EditProfile collects both fields unconditionally, and the
 * existing public profile already renders interests ungated — hiding a value
 * the user just typed reads as a bug here. Reversible in one line if vetoed.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Bell,
  ChevronRight,
  Heart,
  Info,
  MapPin,
  MessageCircle,
  Target,
  User,
  UserX,
  Zap,
} from 'react-feather';
import SEO from '../components/SEO';
import MonoAvatar from '../components/MonoAvatar';
import { useOwnProfile } from '../hooks/useOwnProfile';
import { formatTimestamp, toDateSafe } from '../utils/dateUtils';
import { getMessageUrl } from '../utils/messageUtils';
import { getFeatherIcon } from '../utils/iconMap';
import {
  GENDER_OPTIONS,
  INTERESTED_IN_OPTIONS,
  LOOKING_FOR_OPTIONS,
  ORIENTATION_OPTIONS,
} from '../types/account';
import { BADGE_DEFINITIONS, BADGE_ORDER } from '../types/social';
import type { TimestampLike } from '../types/pplTalk';
import '../styles/profile.css';

type TabId = 'posts' | 'replies' | 'messages';

const TABS: { id: TabId; label: string }[] = [
  { id: 'posts', label: 'posts' },
  { id: 'replies', label: 'replies' },
  { id: 'messages', label: 'messages' },
];

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Enum → label through the options arrays in types/account.ts. Those arrays
 * are what EditProfile writes, so they are the single source; the app's ternary
 * chains are deliberately not duplicated here.
 */
function optionLabel(
  options: readonly { value: string; label: string }[],
  value: string | undefined | null,
): string {
  if (!value) return '';
  return options.find((o) => o.value === value)?.label ?? value;
}

/** dateOfBirth is 'YYYY-MM-DD' on web but a Timestamp for app-made accounts. */
function calculateAge(dateOfBirth: unknown): number | null {
  const birth = toDateSafe(dateOfBirth as TimestampLike);
  if (!birth) return null;
  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  const m = now.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) age -= 1;
  return age > 0 && age < 130 ? age : null;
}

function parseInterests(raw: unknown): string[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map((s) => String(s).trim()).filter(Boolean);
  if (typeof raw === 'string') return raw.split(',').map((s) => s.trim()).filter(Boolean);
  return [];
}

function initialsFrom(source: string): string {
  const letters = source
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
  return letters || '?';
}

function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

// ─── Component ──────────────────────────────────────────────────────────────

export default function Profile() {
  const navigate = useNavigate();
  const {
    profile,
    uid,
    counts,
    badges,
    badgesLoading,
    pendingReviewCount,
    online,
    loading,
    error,
    statsError,
    posts,
    postsLoading,
    hasMorePosts,
    loadMorePosts,
    replies,
    repliesLoading,
    hasMoreReplies,
    loadMoreReplies,
    conversations,
    conversationsLoading,
    hasMoreConversations,
    loadConversations,
    reload,
  } = useOwnProfile();

  const [activeTab, setActiveTab] = useState<TabId>('posts');
  const [badgeTip, setBadgeTip] = useState('');
  const tipTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // The messages tab pays for itself only when it is opened.
  useEffect(() => {
    if (activeTab === 'messages') loadConversations();
  }, [activeTab, loadConversations]);

  useEffect(
    () => () => {
      if (tipTimer.current) clearTimeout(tipTimer.current);
    },
    [],
  );

  // Mirrors the app's handleBadgePress: tap shows, same badge again hides,
  // otherwise it clears itself after 3 seconds.
  const showTip = useCallback(
    (text: string) => {
      if (tipTimer.current) {
        clearTimeout(tipTimer.current);
        tipTimer.current = null;
      }
      if (badgeTip === text) {
        setBadgeTip('');
        return;
      }
      setBadgeTip(text);
      tipTimer.current = setTimeout(() => setBadgeTip(''), 3000);
    },
    [badgeTip],
  );

  // ─── Derived identity ───────────────────────────────────────────────
  const firstName = (profile?.firstName || '').trim();
  const lastName = (profile?.lastName || '').trim();
  const handle = (profile?.tetherHandle || '').replace(/^@/, '').trim();
  const fullName = `${firstName} ${lastName}`.trim() || handle;
  const initials = initialsFrom(`${firstName} ${lastName}`.trim() || handle);
  const city = (profile?.city || '').trim();
  const country = (profile?.country || '').trim();

  // ─── Derived details ────────────────────────────────────────────────
  const age = calculateAge(profile?.dateOfBirth);
  const interests = parseInterests(profile?.interests);
  const aboutMe = (profile?.aboutMe || '').trim();

  // Privacy flags (showAge / showSexualOrientation) are ignored on purpose:
  // they govern what OTHERS see on /user/:userId, and nothing here is visible
  // to anyone else — the same call the app's own-profile screen makes.
  const detailCards: { key: string; icon: JSX.Element; label: string; value: string }[] = [];
  const genderLabel = optionLabel(GENDER_OPTIONS, profile?.gender);
  if (genderLabel) {
    detailCards.push({
      key: 'gender',
      icon: <User size={18} />,
      label: 'gender',
      value: genderLabel,
    });
  }
  if (age !== null) {
    detailCards.push({
      key: 'age',
      icon: <User size={18} />,
      label: 'age',
      value: `${age} years old`,
    });
  }
  const orientationLabel = optionLabel(ORIENTATION_OPTIONS, profile?.sexualOrientation);
  if (orientationLabel) {
    detailCards.push({
      key: 'orientation',
      icon: <Target size={18} />,
      label: 'sexual orientation',
      value: orientationLabel,
    });
  }
  const lookingForLabel = optionLabel(LOOKING_FOR_OPTIONS, profile?.lookingFor);
  if (lookingForLabel) {
    detailCards.push({
      key: 'lookingFor',
      icon: <Target size={18} />,
      label: 'looking for',
      value: lookingForLabel,
    });
  }
  const interestedInLabel = optionLabel(INTERESTED_IN_OPTIONS, profile?.interestedIn);
  if (interestedInLabel) {
    detailCards.push({
      key: 'interestedIn',
      icon: <Heart size={18} />,
      label: 'interested in',
      value: interestedInLabel,
    });
  }
  const hasDetails = detailCards.length > 0 || !!aboutMe || interests.length > 0;

  // ─── Derived badges ─────────────────────────────────────────────────
  const earnedBadgeIds = BADGE_ORDER.filter((id) => badges?.badges?.[id]?.earned);

  // ─── Loading / error ────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="feed">
        <SEO title="my profile" description="your pplkpr profile." path="/profile" noindex />
        <div className="op-shell">
          <div className="profile-loading">
            <div className="profile-skeleton">
              <div className="skeleton-avatar" />
              <div className="skeleton-lines">
                <div className="skeleton-line wide" />
                <div className="skeleton-line medium" />
                <div className="skeleton-line narrow" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="feed">
        <SEO title="my profile" description="your pplkpr profile." path="/profile" noindex />
        <div className="op-shell">
          <div className="op-error">
            <UserX size={32} />
            <h2>couldn&apos;t load your profile</h2>
            <p>{error}</p>
            <button type="button" className="btn btn-secondary" onClick={reload}>
              try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="feed">
      <SEO title="my profile" description="your pplkpr profile." path="/profile" noindex />

      <div className="op-shell">
        {/* ─── 1. Hero card ─────────────────────────────────────────── */}
        <section className="op-hero">
          <div className="op-hero__glow" aria-hidden="true" />

          <div className="op-hero__identity">
            <div className="op-avatar">
              {profile?.profileImageUrl ? (
                <img className="op-avatar__img" src={profile.profileImageUrl} alt="" />
              ) : (
                <div className="op-avatar__mono">{initials}</div>
              )}
              {online && <span className="op-avatar__online" title="online now" />}
            </div>

            <div className="op-hero__names">
              {fullName && <h1 className="op-name">{fullName}</h1>}
              {handle && <div className="op-handle">@{handle}</div>}
              {(city || country) && (
                <div className="op-location">
                  <MapPin size={13} />
                  <span>
                    {city}
                    {city && country ? ', ' : ''}
                    {country}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="op-stats">
            <div className="op-stat">
              <span className="op-stat__num">{counts.posts}</span>
              <span className="op-stat__label">posts</span>
            </div>
            <div className="op-stat">
              <span className="op-stat__num">{counts.replies}</span>
              <span className="op-stat__label">replies</span>
            </div>
            <div className="op-stat">
              <span className="op-stat__num">{counts.likes}</span>
              <span className="op-stat__label">likes</span>
            </div>
            {/* Highlighted, but not a button — web has no viewers page. */}
            <div className="op-stat op-stat--views">
              <span className="op-stat__num">{counts.views}</span>
              <span className="op-stat__label">views</span>
            </div>
          </div>
        </section>

        {/* ─── 2. Intel block ───────────────────────────────────────── */}
        <nav className="op-intel">
          <button type="button" className="op-intel__row" onClick={() => navigate('/settings')}>
            <span className="op-intel__icon">
              <Zap size={16} />
            </span>
            <span className="op-intel__text">
              <span className="op-intel__title">f8 memory</span>
              <span className="op-intel__sub">what f8 keeps, and the switches for it</span>
            </span>
            <span className="op-intel__chevron">
              <ChevronRight size={14} />
            </span>
          </button>

          <button type="button" className="op-intel__row" onClick={() => navigate('/insights')}>
            <span className="op-intel__icon">
              <Bell size={16} />
            </span>
            <span className="op-intel__text">
              <span className="op-intel__title">to review</span>
              <span className="op-intel__sub">things f8 noticed, waiting for your ok</span>
            </span>
            {pendingReviewCount > 0 && (
              <span className="op-intel__badge">
                {pendingReviewCount > 99 ? '99+' : pendingReviewCount}
              </span>
            )}
            <span className="op-intel__chevron">
              <ChevronRight size={14} />
            </span>
          </button>
        </nav>

        {/* ─── 3. Badges ────────────────────────────────────────────── */}
        <section className="op-badges">
          <div className="op-badges__header">
            <span className="op-badges__heading">badges</span>
            {badgeTip && <span className="op-badges__tip">— {badgeTip}</span>}
          </div>

          {earnedBadgeIds.length > 0 ? (
            <div className="op-badges__strip">
              {earnedBadgeIds.map((id) => {
                const def = BADGE_DEFINITIONS[id];
                const stored = badges?.badges?.[id];
                const title = def?.title || stored?.title || id;
                const description = def?.description || stored?.description || title;
                const Icon = getFeatherIcon(def?.icon || 'fa-award');
                return (
                  <button
                    key={id}
                    type="button"
                    className="op-badge"
                    title={title}
                    aria-label={title}
                    onClick={() => showTip(description)}
                  >
                    <Icon size={20} />
                  </button>
                );
              })}
            </div>
          ) : (
            !badgesLoading && <div className="op-badges__empty">earn badges by being active!</div>
          )}
        </section>

        {/* ─── 4. Details ───────────────────────────────────────────── */}
        <section className="op-details">
          {detailCards.map((card) => (
            <div className="op-detail" key={card.key}>
              <span className="op-detail__icon">{card.icon}</span>
              <div className="op-detail__body">
                <div className="op-detail__label">{card.label}</div>
                <div className="op-detail__value">{card.value}</div>
              </div>
            </div>
          ))}

          {aboutMe && (
            <div className="op-detail op-detail--full">
              <span className="op-detail__icon">
                <Info size={18} />
              </span>
              <div className="op-detail__body">
                <div className="op-detail__label">about me</div>
                <div className="op-detail__value">{aboutMe}</div>
              </div>
            </div>
          )}

          {interests.length > 0 && (
            <div className="op-detail op-detail--full">
              <span className="op-detail__icon">
                <Info size={18} />
              </span>
              <div className="op-detail__body">
                <div className="op-detail__label">interests</div>
                <div className="op-detail__tags">
                  {interests.map((tag) => (
                    <span key={tag} className="tag-pill tag-pill--sm tag-pill--pink">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {!hasDetails && (
            <div className="op-detail op-detail--full op-detail--empty">
              <span className="op-detail__icon">
                <Info size={18} />
              </span>
              <div className="op-detail__body">
                <div className="op-detail__value">
                  nothing here yet — add your details with edit profile
                </div>
              </div>
            </div>
          )}
        </section>

        {/* ─── 5. Actions ───────────────────────────────────────────── */}
        <div className="op-actions">
          {uid && (
            <Link className="op-btn" to={`/user/${uid}`}>
              public profile
            </Link>
          )}
          <Link className="op-btn op-btn--primary" to="/profile/edit">
            edit profile
          </Link>
        </div>

        {/* The stats call carries the counts AND both content tabs, so when it
            fails the page would otherwise present zeros as a fact. Say so. */}
        {statsError && (
          <div className="op-notice" role="status">
            <span>couldn&apos;t load your activity</span>
            <button type="button" className="btn btn-secondary" onClick={reload}>
              try again
            </button>
          </div>
        )}

        {/* ─── 6. Tabs ──────────────────────────────────────────────── */}
        <div className="op-tabs" role="tablist">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              className={`op-tab${activeTab === tab.id ? ' op-tab--active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* POSTS */}
        {activeTab === 'posts' && (
          <>
            {posts.length === 0 ? (
              <div className="op-empty">no posts yet.</div>
            ) : (
              <>
                <div className="talk-list">
                  {posts.map((post) => (
                    <Link
                      key={post.id}
                      to={getMessageUrl(post)}
                      className="talk-item"
                      style={{ textDecoration: 'none' }}
                    >
                      <div className="content">
                        {post.title && <h3 className="title">{post.title}</h3>}
                        {post.body && <p className="body">{truncate(post.body, 200)}</p>}
                        {post.categories.length > 0 && (
                          <div className="tag-list">
                            {post.categories.map((c) => (
                              <span key={c} className="tag">
                                {c}
                              </span>
                            ))}
                          </div>
                        )}
                        <div className="meta">
                          <span className="muted">
                            <Heart size={14} /> {post.likes}
                          </span>
                          <span className="muted">
                            <MessageCircle size={14} /> {post.replies}
                          </span>
                          <span className="dot">·</span>
                          <span className="muted">posted by me</span>
                          <span className="dot">·</span>
                          <span className="muted">{formatTimestamp(post.timestamp)}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>

                {hasMorePosts && (
                  <div className="op-more">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={loadMorePosts}
                      disabled={postsLoading}
                    >
                      {postsLoading ? 'loading…' : 'load more'}
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* REPLIES */}
        {activeTab === 'replies' && (
          <>
            {replies.length === 0 ? (
              <div className="op-empty">no replies yet.</div>
            ) : (
              <>
                <div className="talk-list">
                  {replies.map((reply) => {
                    const inner = (
                      <div className="content">
                        {reply.originalMessageTitle && (
                          <span className="op-reply-to">
                            reply to: {reply.originalMessageTitle}
                          </span>
                        )}
                        <p className="body">{truncate(reply.text, 200)}</p>
                        {reply.originalMessageCategories &&
                          reply.originalMessageCategories.length > 0 && (
                            <div className="tag-list">
                              {reply.originalMessageCategories.map((c) => (
                                <span key={c} className="tag">
                                  {c}
                                </span>
                              ))}
                            </div>
                          )}
                        <div className="meta">
                          <span className="muted">
                            <Heart size={14} /> {reply.likes}
                          </span>
                          <span className="dot">·</span>
                          <span className="muted">replied by me</span>
                          <span className="dot">·</span>
                          <span className="muted">{formatTimestamp(reply.timestamp)}</span>
                        </div>
                      </div>
                    );

                    // No original post id = nowhere to go; a dead Link is worse
                    // than a row that simply does not navigate.
                    return reply.originalMessageId ? (
                      <Link
                        key={reply.id}
                        to={getMessageUrl({
                          id: reply.originalMessageId,
                          title: reply.originalMessageTitle,
                        })}
                        className="talk-item"
                        style={{ textDecoration: 'none' }}
                      >
                        {inner}
                      </Link>
                    ) : (
                      <div key={reply.id} className="talk-item" style={{ cursor: 'default' }}>
                        {inner}
                      </div>
                    );
                  })}
                </div>

                {hasMoreReplies && (
                  <div className="op-more">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={loadMoreReplies}
                      disabled={repliesLoading}
                    >
                      {repliesLoading ? 'loading…' : 'load more'}
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* MESSAGES — ANONYMOUS half: @handle only, never a real name. */}
        {activeTab === 'messages' && (
          <>
            {conversationsLoading && conversations.length === 0 ? (
              <div className="op-empty">loading…</div>
            ) : conversations.length === 0 ? (
              <div className="op-empty">no conversations yet.</div>
            ) : (
              <>
                <div className="op-convos">
                  {conversations.map((c) => (
                    <button
                      key={c.chatId}
                      type="button"
                      className={`op-convo${c.unread ? ' op-convo--unread' : ''}`}
                      onClick={() =>
                        navigate(`/messages/${c.chatId}`, {
                          state: { recipientId: c.recipientId, recipientName: c.recipientName },
                        })
                      }
                    >
                      {c.recipientPhotoURL ? (
                        <img className="op-convo__avatar" src={c.recipientPhotoURL} alt="" />
                      ) : (
                        <MonoAvatar name={c.recipientName} size={36} />
                      )}
                      <span className="op-convo__body">
                        <span className="op-convo__nameRow">
                          <span className="op-convo__name">@{c.recipientName}</span>
                          {c.unread && <span className="op-convo__dot" />}
                        </span>
                        {c.lastMessage && (
                          <span className="op-convo__preview">{c.lastMessage}</span>
                        )}
                      </span>
                      <span className="op-convo__time">{formatTimestamp(c.lastMessageAt)}</span>
                    </button>
                  ))}
                </div>

                {/* messageService drops the callable's cursor, so the overflow
                    goes to the real messages page rather than a load-more. */}
                {hasMoreConversations && (
                  <div className="op-more">
                    <Link to="/messages">see all in messages</Link>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
